import * as React from 'react';

export type ReMatterLogoSize = 'small' | 'large';

type ReMatterLogoProps = {
  size?: ReMatterLogoSize;
  height?: number;
};

const SMALL_DIMENSIONS = { width: 45, height: 45 };
const LARGE_DIMENSIONS = { width: 255, height: 94 };

export const ReMatterLogo: React.FC<ReMatterLogoProps> = ({
  size = 'large',
  height,
}) =>
  size === 'small' ? (
    <SmallReMatterLogo height={height} />
  ) : (
    <LargeReMatterLogo height={height} />
  );

const SmallReMatterLogo: React.FC<ReMatterLogoProps> = ({
  height = SMALL_DIMENSIONS.height,
}) => {
  const width = (height / SMALL_DIMENSIONS.height) * SMALL_DIMENSIONS.width;
  const backgroundColor = 'transparent'; // theme === 'light' ? 'white' : 'black';

  return (
    <svg
      width={width}
      height={height}
      viewBox="77 77 358 358"
      fill="none"
      className="@SmallReMatterLogo"
    >
      <rect // Background
        x="106"
        y="106"
        width="300"
        height="300"
        fill={backgroundColor}
      />
      <path // Border
        className={'fill-primary'}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M354.679 106.764H157.321C129.399 106.764 106.764 129.399 106.764 157.321V354.679C106.764 382.601 129.399 405.236 157.321 405.236H354.679C382.601 405.236 405.236 382.601 405.236 354.679V157.321C405.236 129.399 382.601 106.764 354.679 106.764ZM157.321 77C112.961 77 77 112.961 77 157.321V354.679C77 399.039 112.961 435 157.321 435H354.679C399.039 435 435 399.039 435 354.679V157.321C435 112.961 399.039 77 354.679 77H157.321Z"
      />
      <path // Letter R
        // fill={lettersColor}
        d="M250.991 288.415L255.059 329.305H222.939L218.871 292.653C218.3 287.568 216.301 283.472 212.875 280.364C209.592 277.116 205.452 275.492 200.456 275.492H171.334V329.305H139V181H202.169C217.872 181 230.22 185.308 239.213 193.924C248.207 202.54 252.704 213.768 252.704 227.61C252.704 241.734 246.422 252.045 233.86 258.542C243.996 265.463 249.706 275.421 250.991 288.415ZM171.334 207.483V248.161H200.456C207.451 248.161 212.732 246.184 216.301 242.229C219.87 238.274 221.655 233.401 221.655 227.61C221.655 221.819 219.87 217.017 216.301 213.203C212.732 209.39 207.451 207.483 200.456 207.483H171.334Z"
        className={'fill-current'}
      />
      <path // Letter e
        // fill={lettersColor}
        d="M343.878 293.076H373C370.859 304.517 365.077 313.698 355.655 320.619C346.234 327.54 334.885 331 321.609 331C304.335 331 290.774 325.492 280.924 314.475C271.074 303.316 266.149 290.393 266.149 275.703C266.149 260.873 270.717 248.02 279.853 237.144C289.132 226.268 302.337 220.831 319.467 220.831C337.026 220.831 350.302 226.056 359.296 236.508C368.289 246.96 372.786 259.107 372.786 272.949C372.786 276.198 372.643 279.376 372.358 282.483H294.842C295.984 290.11 298.839 296.113 303.407 300.492C308.118 304.729 314.185 306.847 321.609 306.847C334.028 306.847 341.451 302.257 343.878 293.076ZM319.467 242.441C305.763 242.441 297.697 249.573 295.27 263.839H343.022C342.593 257.624 340.309 252.54 336.169 248.585C332.03 244.489 326.462 242.441 319.467 242.441Z"
        className={'fill-current'}
      />
    </svg>
  );
};

const LargeReMatterLogo: React.FC<ReMatterLogoProps> = ({
  height = LARGE_DIMENSIONS.height,
}) => {
  const width = (height / LARGE_DIMENSIONS.height) * LARGE_DIMENSIONS.width;

  return (
    <svg
      viewBox="0 0 255 94"
      width={width}
      height={height}
      fill="none"
      className="@LargeReMatterLogo"
    >
      <path // Word: ReMatter
        className={'fill-current'}
        d="m49.593 55.771 1.065 10.782h-8.407l-1.065-9.665c-.15-1.34-.672-2.42-1.57-3.24-.858-.856-1.942-1.285-3.25-1.285h-7.622v14.22H20.28l-.027-39.161h16.534c4.11 0 7.369 1.162 9.723 3.434 2.354 2.272 3.53 5.233 3.53 8.882 0 3.724-1.643 6.443-4.931 8.156 2.652 1.825 4.147 4.45 4.483 7.877Zm-20.85-21.34v10.726h7.623c1.831 0 3.214-.521 4.148-1.564.934-1.043 1.4-2.328 1.4-3.855 0-1.527-.466-2.793-1.4-3.798-.934-1.006-2.317-1.509-4.148-1.509h-7.622ZM73.906 56.554h7.622c-.56 3.016-2.074 5.437-4.54 7.262-2.466 1.825-5.436 2.737-8.911 2.737-4.521 0-8.07-1.452-10.649-4.357-2.578-2.942-3.867-6.35-3.867-10.223 0-3.91 1.196-7.3 3.587-10.167 2.428-2.868 5.885-4.302 10.368-4.302 4.596 0 8.071 1.378 10.425 4.134 2.354 2.756 3.531 5.96 3.531 9.609 0 .856-.037 1.694-.112 2.514H61.071c.299 2.01 1.046 3.593 2.242 4.748 1.233 1.117 2.821 1.676 4.764 1.676 3.25 0 5.194-1.21 5.829-3.631Zm-6.39-13.352c-3.587 0-5.698 1.881-6.333 5.643h12.499c-.112-1.639-.71-2.98-1.794-4.023-1.083-1.08-2.54-1.62-4.372-1.62ZM86.563 66.553V27.421h9.472l11.433 27.312 11.49-27.317h9.416v39.137h-7.847V42.667l-9.92 23.886h-6.221l-9.977-23.886v23.886h-7.846ZM146.588 37.462c3.587 0 6.539.968 8.855 2.904 2.317 1.9 3.475 4.432 3.475 7.598V66.52h-7.734v-4.367c-.486 1.304-1.458 2.365-2.915 3.184-1.42.782-3.101 1.222-5.044 1.222-2.728 0-4.951-.85-6.67-2.45-1.718-1.602-2.578-3.613-2.578-6.034 0-2.756.934-4.916 2.803-6.48 1.905-1.601 4.409-2.402 7.51-2.402h4.764c.672 0 1.195-.186 1.569-.559.374-.41.561-.912.561-1.508 0-1.192-.449-2.141-1.346-2.849-.896-.745-2.129-1.117-3.699-1.117-1.606 0-2.877.428-3.811 1.285-.897.856-1.382 1.917-1.457 3.184h-6.95c.187-2.942 1.401-5.363 3.643-7.262 2.279-1.937 5.287-2.905 9.024-2.905Zm-1.401 23.406c1.868 0 3.325-.596 4.371-1.787 1.084-1.192 1.626-2.719 1.626-4.581v-.391h-5.717c-1.196 0-2.167.335-2.915 1.005-.747.67-1.121 1.546-1.121 2.626 0 .931.355 1.695 1.065 2.29.71.56 1.607.838 2.691.838ZM202.448 44.845h-7.566v11.117c0 2.681 1.308 4.022 3.923 4.022h3.643v6.58h-4.652c-3.288 0-5.903-.938-7.846-2.725-1.943-1.788-2.915-4.302-2.915-7.542V44.845h-11.938v11.117c0 2.681 1.327 4.022 3.98 4.022h3.587v6.58h-4.652c-3.288 0-5.904-.938-7.847-2.725-1.905-1.788-2.858-4.302-2.858-7.542V44.845h-5.437v-6.48h5.437v-8.324h7.79v8.324h11.938v-8.324h7.847v8.324h7.566v6.48ZM225.025 56.56h7.622c-.561 3.016-2.074 5.437-4.54 7.262-2.466 1.825-5.436 2.737-8.911 2.737-4.521 0-8.071-1.453-10.649-4.357-2.578-2.943-3.867-6.35-3.867-10.223 0-3.91 1.195-7.3 3.587-10.168 2.428-2.867 5.884-4.301 10.368-4.301 4.596 0 8.071 1.378 10.425 4.134 2.354 2.756 3.531 5.959 3.531 9.608 0 .857-.038 1.695-.112 2.514H212.19c.299 2.011 1.046 3.594 2.242 4.749 1.233 1.117 2.821 1.675 4.764 1.675 3.25 0 5.193-1.21 5.829-3.63Zm-6.39-13.352c-3.587 0-5.698 1.88-6.333 5.642H224.8c-.112-1.638-.71-2.98-1.793-4.022-1.084-1.08-2.541-1.62-4.372-1.62ZM255 44.884h-4.652c-1.868 0-3.269.708-4.203 2.123-.935 1.415-1.402 3.37-1.402 5.866v13.694h-7.846V38.404h7.846v4.692c.748-1.713 1.663-2.923 2.747-3.63 1.121-.708 2.559-1.062 4.315-1.062H255v6.48Z"
      />
      <path // Border around [Re]
        className={'fill-primary'}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21.175 7.815h52.032c7.36 0 13.328 5.943 13.328 13.275h7.847C94.382 9.442 84.902 0 73.207 0H21.176C9.48 0 0 9.442 0 21.09v51.82C0 84.558 9.48 94 21.175 94h52.032c11.695 0 21.175-9.442 21.175-21.09h-7.847c0 7.332-5.967 13.275-13.328 13.275H21.176c-7.362 0-13.329-5.943-13.329-13.275V21.09c0-7.332 5.967-13.275 13.328-13.275Z"
      />
    </svg>
  );
};
